
import { useState, useEffect, useRef } from 'react';

interface TocItem {
  id: string;
  text: string;
}

interface TableOfContentsProps {
  items: TocItem[];
}

export default function TableOfContents({ items = [] }: TableOfContentsProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [activeId, setActiveId] = useState('');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 }
    );

    items.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el && observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [items]);

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Table of Contents"
      className="bg-gray-50 border border-gray-200 rounded-2xl p-6 my-10"
      itemScope
      itemType="https://schema.org/SiteNavigationElement"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full cursor-pointer"
        aria-expanded={isOpen}
      >
        <h2 className="text-lg font-bold text-black flex items-center gap-2 m-0">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-list-unordered text-[#D4AF37]"></i>
          </span>
          Table of Contents
        </h2>
        <span className="w-6 h-6 flex items-center justify-center text-gray-500">
          <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-lg`}></i>
        </span>
      </button>

      {isOpen && (
        <ol className="mt-4 space-y-1 list-none pl-0">
          {items.map((item, index) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(item.id);
                }}
                className={`flex items-start gap-3 py-2 px-3 rounded-lg text-sm leading-snug transition-all cursor-pointer hover:bg-white hover:shadow-sm ${
                  activeId === item.id
                    ? 'bg-white shadow-sm text-black font-semibold'
                    : 'text-gray-600 hover:text-black'
                }`}
                itemProp="url"
              >
                <span className={`text-xs font-bold mt-0.5 min-w-[20px] ${activeId === item.id ? 'text-[#D4AF37]' : 'text-gray-400'}`}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span itemProp="name">{item.text}</span>
              </a>
            </li>
          ))}
        </ol>
      )}
    </nav>
  );
}

export function generateTocItems(headings: string[]): TocItem[] {
  return headings.map((text) => ({
    id: text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 80),
    text,
  }));
}
